import * as React from "react"
import {useEffect, useMemo, useRef, useState} from "react"
import {useAppDispatch, useDimensions, useDimensionsPending} from "../redux/stateHooks";
import {debounce} from "lodash";
import {setDimensions, setPendingDimensions} from "../redux/slices/addToCart";
import {IDimension} from "../areas/product/purchase/dynamicDimensions";

export interface IDimensionPending {
    dimension: 'width' | 'height';
    width: boolean;
    height: boolean;
}

export const useDimensionChange = ({dimension, min, max, value}: IDimension) => {
    const dispatch = useAppDispatch();
    const dimensions = useDimensions();
    const [localValue, setLocalValue] = useState<number>(value);

    // If the value is less than the min or greater than the max, it is invalid
    const invalid = useMemo(() => localValue < min || localValue > max, [localValue, min, max]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        // If the user enters a value that is not a number, set it to the min
        if (isNaN(value)) value = min;

        // Update the local state with the new value, intentionally not normalized to the min/max values
        setLocalValue(value);

        // Set the pending state to true
        dispatch(setPendingDimensions({
            [dimension]: true
        }))

        // Normalize the value to prevent invalid values
        const normalizedDimension = value < min || value > max ? undefined : value;

        // Update the global state with the new value or undefined if it is invalid (debounced)
        debounceChange(normalizedDimension);
    };

    // When the user changes the value, update the dimension in the redux store and debounce the change
    const debounceChange = useMemo(() => debounce((value?: number) => {
        // Update the specific dimension in global state
        dispatch(setDimensions({
            [dimension]: value
        }));

        // Set the pending state to false
        return dispatch(setPendingDimensions({
            [dimension]: false
        }))
    }, 500), [dimensions, invalid]);

    // When the component unmounts, cancel the debounced function
    useEffect(() => () => debounceChange.cancel(), []);

    return {
        onChange,
        dimension,
        range: {
            min,
            max
        },
        values: {
            local: localValue,
            global: dimensions[dimension]
        },
        status: {
            valid: !invalid,
            invalid
        }
    }
}